import * as React from "react";
import { useEffect } from "react";
import {
    DrawerBody,
    DrawerHeader,
    Drawer,
    Button,
    useRestoreFocusSource,
    useRestoreFocusTarget,
    ToggleButton,
    DrawerFooter,
    ProgressBar,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import {
    Avatar,
    Divider,
    Flex,
    Text,
    Button as ButtonNS,
    AcceptIcon,
    CloseIcon,
    EditIcon,
    InputLabel,
    Dialog,
} from "@fluentui/react-northstar";
import styled, { css } from "styled-components";
import { useMutation, useQuery } from "react-query";
import {
    _api_get_detail_factsheet,
    _api_send_approve,
    _api_send_refuse,
    _api_send_reminder,
} from "../../api";
import LoaderPage from "../../../../components/loaders/LoaderPage";
import { CellStatus, STATUS_DROPDOWN } from "./TableFactsheet";
import AlertInfo from "../../../../components/Alerts/Info";
import moment from "moment";
import { toast } from "sonner";
import { useRef } from "react";
import { useState } from "react";
import { queryClient } from "../../../..";
import DialogExportFactsheet from "./DialogExportFactsheet";

const formatDateTime = (dateString) => {
    if (!dateString) return "--";
    const d = new Date(dateString);
    const date = new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(d);
    const time = new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    })
        .format(d)
        .replace(":", "h");
    return `${date} à ${time}`;
};

const columns = [
    {
        columnKey: "field",
        label: "CHAMP",
    },
    {
        columnKey: "value",
        label: "Valeur saisie",
    },
    {
        columnKey: "status",
        label: "Statut",
    },
];

export const DrawerFactsheetDetail = ({
    isOpen = false,
    setIsOpen = () => null,
    selectedUserDetail = null,
}) => {
    const restoreFocusSourceAttributes = useRestoreFocusSource();
    const [wantAccept, setWantAccept] = useState(null);
    const [comment, setComment] = useState("");
    const endOfFormRef = useRef(null);
    const commentRef = useRef(null);

    useEffect(() => {
        if (wantAccept === false) {
            requestAnimationFrame(() => {
                commentRef.current?.focus();
            });
        }
    }, [wantAccept]);

    const { data: detailFactsheet, isLoading } = useQuery(
        ["getDetailFactsheet", selectedUserDetail?.id],
        () =>
            _api_get_detail_factsheet({
                selectedUserId: selectedUserDetail?.id,
            }),
        {
            enabled: selectedUserDetail?.id !== null,
        }
    );
    // reminder
    const { mutate: sendReminderMutate, isLoading: isSendingReminder } =
        useMutation((data) => _api_send_reminder(data));
    // refuse
    const { mutate: sendRefuseMutate, isLoading: isSendingRefuse } =
        useMutation((data) => _api_send_refuse(data));
    // approve
    const { mutate: sendApproveMutate, isLoading: isSendingApprove } =
        useMutation((data) => _api_send_approve(data));

    const closeDrawer = (_, { open }) => {
        setWantAccept(null);
        setIsOpen(open);
        return;
    };
    const closeDrawerManually = () => closeDrawer(null, { open: false });

    const invalidateFactsheetQueries = () => {
        queryClient.invalidateQueries([
            "getDetailFactsheet",
            selectedUserDetail?.id,
        ]);
        queryClient.invalidateQueries(["factsheets"]);
    };

    const buildMutationCallbacks = (successMsg, errorMsg, onSuccessExtra) => ({
        onSuccess: (data) => {
            if (data?.success) {
                onSuccessExtra?.();
                toast.success(successMsg, { position: "bottom-right" });
            } else {
                toast.error(errorMsg, { position: "bottom-right" });
            }
        },
    });

    const sendReminder = () => {
        sendReminderMutate(
            { id_user: selectedUserDetail?.id },
            buildMutationCallbacks(
                `Relance envoyée à ${selectedUserDetail?.full_name}.`,
                `Erreur lors de l'envoi de la relance à ${selectedUserDetail?.full_name}.`,
                invalidateFactsheetQueries
            )
        );
    };

    const sendRefuse = () => {
        sendRefuseMutate(
            { id_user: selectedUserDetail?.id, reason: comment },
            buildMutationCallbacks(
                `Refus envoyé à ${selectedUserDetail?.full_name}.`,
                `Erreur lors de l'envoi du refus à ${selectedUserDetail?.full_name}.`,
                invalidateFactsheetQueries
            )
        );
    };

    const sendApprove = () => {
        sendApproveMutate(
            { id_user: selectedUserDetail?.id },
            buildMutationCallbacks(
                `Acceptation envoyée à ${selectedUserDetail?.full_name}.`,
                `Erreur lors de l'envoi de l'acceptation à ${selectedUserDetail?.full_name}.`,
                invalidateFactsheetQueries
            )
        );
    };

    const handleChangeComment = (e) => {
        setComment(e.target.value);
    };

    const scrollToEndDrawer = () => {
        if (endOfFormRef.current) {
            endOfFormRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
        }
    };

    const isLoadingData = isLoading;
    const status =
        detailFactsheet?.completeness_status !== undefined
            ? STATUS_DROPDOWN.find(
                  (s) => s.id === detailFactsheet?.completeness_status
              )
            : "--";
    const lastSaveDateTime = formatDateTime(
        detailFactsheet?.completeness_updated_at
    );
    const submittedAt = formatDateTime(
        detailFactsheet?.completeness_submitted_at
    );
    const formatDueDate = detailFactsheet?.task_due_date
        ? moment(detailFactsheet?.task_due_date).format("DD/MM/YYYY")
        : "--";
    const isFilling = detailFactsheet?.completeness_status === 0;
    const isSubmitted = detailFactsheet?.completeness_status === 2;
    const isApproved = detailFactsheet?.completeness_status === 1;
    const isRefused = detailFactsheet?.completeness_status === 3;
    const isFullProgressBar = detailFactsheet?.completeness_percentage === 100;
    const canExportFactsheet = !isFilling;
    const rejected_reason = detailFactsheet?.rejected_reason;
    const formatedRows = detailFactsheet?.fields?.map((field) => ({
        field: { label: field.label },
        value: { label: field.value },
        is_filled: { value: field.is_completed },
    }));

    return (
        <div>
            <Drawer
                position="end"
                {...restoreFocusSourceAttributes}
                type={"overlay"}
                separator
                open={isOpen}
                onOpenChange={closeDrawer}
                size="medium"
                style={{
                    width: "700px",
                }}
            >
                {isLoadingData ? (
                    <LoaderPage label="Chargement de la fiche ..." />
                ) : !selectedUserDetail ? (
                    <p>404 not found</p>
                ) : (
                    <>
                        <DrawerHeader>
                            <Flex fill space="between">
                                <Flex column gap="gap.small">
                                    <Flex gap="gap.smaller" vAlign="center">
                                        <Flex>
                                            <Avatar
                                                name={
                                                    selectedUserDetail.full_name
                                                }
                                                image={selectedUserDetail.image}
                                            />
                                        </Flex>
                                        <Flex column>
                                            <STextFullname
                                                content={
                                                    selectedUserDetail.full_name
                                                }
                                            />
                                            <STextJob
                                                content={
                                                    selectedUserDetail.job_title
                                                }
                                            />
                                        </Flex>
                                    </Flex>
                                    <Flex vAlign="center" gap="gap.smaller">
                                        <CellStatus status={status} />
                                        {isFilling && (
                                            <STextDateSave
                                                content={`Dernière sauvegarde : ${lastSaveDateTime}`}
                                            />
                                        )}
                                        {isSubmitted && (
                                            <STextDateSave
                                                content={`Reçu le : ${submittedAt}`}
                                            />
                                        )}
                                    </Flex>
                                </Flex>
                                <Flex vAlign="start">
                                    <Button
                                        appearance="subtle"
                                        aria-label="Close"
                                        icon={<Dismiss24Regular />}
                                        onClick={closeDrawerManually}
                                    />
                                </Flex>
                            </Flex>
                            <Divider />
                        </DrawerHeader>

                        <DrawerBody>
                            <Flex
                                column
                                gap="gap.small"
                                style={{ paddingBottom: "16px" }}
                            >
                                {isFilling && (
                                    <Flex column>
                                        <SectionTitle content="Suivi de la complétude — Brouillon en cours" />
                                        <AlertInfo
                                            title="Formulaire enregistré en brouillon — non encore soumis"
                                            content="La fiche ne sera transmise pour validation qu'une fois tous les champs obligatoires renseignés et validés."
                                            footer={
                                                <Flex
                                                    gap="gap.small"
                                                    styles={{
                                                        marginTop: "16px",
                                                    }}
                                                >
                                                    <Flex gap="gap.smaller">
                                                        <Text content="Échéance : " />
                                                        <Text
                                                            content={
                                                                <b>
                                                                    {
                                                                        formatDueDate
                                                                    }
                                                                </b>
                                                            }
                                                        />
                                                    </Flex>
                                                    <Flex>·</Flex>
                                                    <Flex gap="gap.smaller">
                                                        <Text content="Avancement :" />
                                                        <Text
                                                            content={
                                                                <b>
                                                                    {
                                                                        detailFactsheet?.completeness_percentage
                                                                    }
                                                                    %
                                                                </b>
                                                            }
                                                        />
                                                    </Flex>
                                                </Flex>
                                            }
                                        />
                                    </Flex>
                                )}
                                {/* progressBar */}
                                <Flex column>
                                    <ProgressBar
                                        value={
                                            detailFactsheet?.completeness_percentage /
                                            100
                                        }
                                        color={
                                            isFullProgressBar
                                                ? "success"
                                                : "brand"
                                        }
                                        style={{
                                            height: "8px",
                                        }}
                                    />
                                    <STextProgressValue
                                        content={
                                            isFullProgressBar
                                                ? `100% des champs obligatoires renseignés ✓`
                                                : `${detailFactsheet?.completeness_percentage}% — ${detailFactsheet?.missing_informations?.length} champ(s) obligatoire manquant(s) `
                                        }
                                        $success={isFullProgressBar}
                                    />
                                </Flex>
                                {/* Table fields */}
                                <STable
                                    size="small"
                                    aria-label="Table with small size"
                                    style={{ minWidth: "475px" }}
                                >
                                    <STableHeader>
                                        <TableRow>
                                            {columns.map((column, index) => (
                                                <STableHeaderCell
                                                    key={column.columnKey}
                                                    style={{
                                                        width:
                                                            index === 2
                                                                ? "17%"
                                                                : "auto",
                                                    }}
                                                >
                                                    {column.label}
                                                </STableHeaderCell>
                                            ))}
                                        </TableRow>
                                    </STableHeader>
                                    <TableBody>
                                        {formatedRows.map((item, index) => (
                                            <STableRow
                                                key={index}
                                                $ValueSet={item.is_filled.value}
                                            >
                                                <TableCell>
                                                    <STextField
                                                        content={
                                                            item.field.label
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {!item.value.label ? (
                                                        <STextValueEmpty content="Non renseigné" />
                                                    ) : (
                                                        <STextValue
                                                            content={
                                                                item.value.label
                                                            }
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <SFlex
                                                        $ValueSet={
                                                            item.is_filled.value
                                                        }
                                                        vAlign="center"
                                                        gap="gap.smaller"
                                                    >
                                                        {item.is_filled
                                                            .value ? (
                                                            <AcceptIcon size="smaller" />
                                                        ) : (
                                                            <EditIcon
                                                                outline
                                                                size="smaller"
                                                            />
                                                        )}
                                                        <Text
                                                            content={
                                                                item.is_filled
                                                                    .value
                                                                    ? "Renseigné"
                                                                    : "À compléter"
                                                            }
                                                        />
                                                    </SFlex>
                                                </TableCell>
                                            </STableRow>
                                        ))}
                                    </TableBody>
                                </STable>
                                {/* afficher le commentaire */}
                                {(isFilling || isRefused) && (
                                    <Flex column>
                                        {rejected_reason && (
                                            <AlertInfo
                                                error
                                                content={`Commentaire : ${rejected_reason}`}
                                            />
                                        )}
                                    </Flex>
                                )}
                                {/* Décision de validation */}
                                {isSubmitted && (
                                    <>
                                        <Flex column>
                                            <SectionTitle content="Décision de validation" />
                                            <Flex gap="gap.smaller">
                                                <SButtonNS
                                                    flat
                                                    content="Accepter la fiche"
                                                    icon={
                                                        <AcceptIcon size="smaller" />
                                                    }
                                                    fluid
                                                    onClick={() => {
                                                        setWantAccept(true);
                                                        scrollToEndDrawer();
                                                    }}
                                                    className={
                                                        wantAccept === true
                                                            ? "active"
                                                            : ""
                                                    }
                                                />
                                                <SButtonNS
                                                    flat
                                                    content="Refuser"
                                                    $refuse
                                                    icon={
                                                        <CloseIcon size="smaller" />
                                                    }
                                                    fluid
                                                    onClick={() => {
                                                        setWantAccept(false);
                                                        scrollToEndDrawer();
                                                    }}
                                                    className={
                                                        wantAccept === false
                                                            ? "active"
                                                            : ""
                                                    }
                                                />
                                            </Flex>
                                        </Flex>
                                        <Flex column gap="gap.small">
                                            {/* commentaire */}
                                            <div
                                                style={{
                                                    display:
                                                        wantAccept !== null &&
                                                        wantAccept === false
                                                            ? "block"
                                                            : "none",
                                                }}
                                            >
                                                <SInputLabel content="Commentaire (optionnel)" />
                                                <STextArea
                                                    ref={commentRef}
                                                    placeholder="Ajoutez un commentaire à destination du collaborateur..."
                                                    onChange={
                                                        handleChangeComment
                                                    }
                                                />
                                                <STextHint content="Ce message sera visible par le collaborateur dans son espace Yeetch et transmis par email." />
                                            </div>
                                        </Flex>
                                    </>
                                )}
                            </Flex>
                            <div ref={endOfFormRef}></div>
                        </DrawerBody>
                        <DrawerFooter
                            style={{
                                borderTop: "1px solid var(--border)",
                            }}
                        >
                            <SFlexFooter fill space="between" vAlign="center">
                                <SButtonClose
                                    content="Fermer"
                                    flat
                                    onClick={closeDrawerManually}
                                />
                                <Flex gap="gap.smaller" vAlign="center">
                                    {canExportFactsheet && (
                                        <DialogExportFactsheet
                                            id_user={selectedUserDetail?.id}
                                        />
                                    )}
                                    {wantAccept === true && !isApproved && (
                                        <SButtonConfirmValidation
                                            content="✓ Confirmer la validation"
                                            flat
                                            onClick={sendApprove}
                                            loading={isSendingApprove}
                                        />
                                    )}
                                    {wantAccept === false && !isRefused && (
                                        <SButtonConfirmValidation
                                            content="↩ Confirmer le refus & relancer"
                                            onClick={sendRefuse}
                                            loading={isSendingRefuse}
                                            flat
                                            $error
                                        />
                                    )}
                                    {(isFilling || isRefused) && (
                                        <SButtonRelance
                                            flat
                                            content="📧 Envoyer une relance maintenant"
                                            onClick={sendReminder}
                                            loading={isSendingReminder}
                                        />
                                    )}
                                    {/* statut en cours */}
                                    {isFilling && (
                                        <STextFooterDescription content="Validation disponible une fois la fiche soumise." />
                                    )}
                                </Flex>
                            </SFlexFooter>
                        </DrawerFooter>
                    </>
                )}
            </Drawer>
        </div>
    );
};

const SButtonConfirmValidation = styled(ButtonNS)`
    background: ${({ $error }) => ($error ? "var(--red)" : "var(--green)")};
    color: white;

    font-size: 13px;
    padding: 9px 18px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-weight: 600;
    font-family: inherit;
    transition: all 0.12s;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const STextHint = styled(Text)`
    font-size: 11px;
    color: var(--text-tert);
    margin-top: 5px;
    line-height: 1.5;
`;
const STextArea = styled.textarea`
    width: 100%;
    min-height: 96px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 10px 12px;
    font-size: 13px;
    font-family: inherit;
    color: var(--text);
    background: var(--bg-secondary);
    resize: vertical;
    outline: none;
    transition: border-color 0.12s;

    &:focus {
        border-color: var(--purple);
        background: white;
    }
`;
const SInputLabel = styled(InputLabel)`
    font-size: 11px;
    font-weight: 600;
    color: var(--text-sec);
    margin-bottom: 6px;
`;
const STextFooterDescription = styled(Text)`
    font-size: 11px;
    color: var(--text-tert);
    font-style: italic;
`;
const SButtonClose = styled(ButtonNS)`
    background: var(--bg-card);
    color: var(--text);
    border: 1px solid var(--border-mid);
`;
const SFlexFooter = styled(Flex)`
    padding: 0px 0px;
    flex-shrink: 0;
`;
const STextValueEmpty = styled(Text)`
    color: var(--blue);
    font-style: italic;
    font-weight: 400;
    font-size: 12px;
`;
const STableRow = styled(TableRow)`
    background: ${({ $ValueSet }) =>
        $ValueSet ? "inherit" : "var(--blue-light)"};
`;
const SFlex = styled(Flex)`
    width: fit-content;
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 10px;
    font-weight: 600;
    white-space: nowrap;
    ${({ $ValueSet }) =>
        $ValueSet
            ? css`
                  background: var(--green-light);
                  color: var(--green-dark);
              `
            : css`
                  background: var(--blue-light);
                  color: var(--blue);
              `}
`;
const STextValue = styled(Text)`
    font-size: 12px;
    color: var(--text);
    font-weight: 600;
`;
const STextField = styled(Text)`
    font-size: 12px;
    color: var(--text-sec);
`;
const STableHeaderCell = styled(TableHeaderCell)`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-tert);
`;
const STableHeader = styled(TableHeader)`
    background: var(--bg-secondary);
`;
const STable = styled(Table)`
    outline: 1px solid var(--border) !important;
    border-radius: var(--radius);
    overflow: hidden;
`;
const SButtonNS = styled(ButtonNS)`
    padding: 20px 0px !important;
    border-radius: var(--radius-sm);
    border: 1.5px solid var(--border);
    font-size: 13px;
    background: var(--bg-secondary);
    color: var(--text-sec);
    &.active,
    &:hover {
        ${({ $refuse }) =>
            $refuse
                ? css`
                      background: var(--red-light);
                      color: var(--red);
                      border-color: var(--red-mid);
                  `
                : css`
                      background: var(--green-light);
                      color: var(--green-dark);
                      border-color: var(--green-mid);
                  `}
    }
`;
const SButtonRelance = styled(ButtonNS)`
    background: var(--amber-light);
    color: var(--amber);
    border: 1.5px solid var(--amber-mid);
    font-size: 13px;
    padding: 9px 18px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-weight: 600;
    font-family: inherit;
    transition: all 0.12s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    :hover {
        background: var(--amber-mid);
        color: white;
    }
`;
const STextProgressValue = styled(Text)`
    font-size: 11px;
    color: ${({ $success }) =>
        $success ? "var(--green-dark)" : "var(--blue)"};
    font-weight: 600;
    margin-bottom: 8px;
`;
const SectionTitle = styled(Text)`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-tert);
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border);
`;

const STextDateSave = styled(Text)`
    font-size: 12px;
    color: var(--text-tert);
`;
const STextFullname = styled(Text)`
    font-size: 16px;
    font-weight: 700;
`;
const STextJob = styled(Text)`
    font-size: 12px;
    color: var(--text-tert);
`;
